/**
 * Game utility functions following SOLID principles
 * Single Responsibility: Each function has one clear purpose
 */

/**
 * Formats content by replacing line breaks with HTML br tags
 * @param {string} content - The content to format
 * @returns {string} Formatted content with HTML line breaks
 */
export const formatContent = (content) => {
  if (!content) return '';
  return content.replaceAll('\n', '<br>');
};

/**
 * Selects random content from an array
 * @param {Array} contents - Array of content options
 * @returns {string} Randomly selected content or empty string
 */
export const selectRandomContent = (contents) => {
  if (!contents || !contents.length) return '';
  return contents[Math.floor(Math.random() * contents.length)];
};

/**
 * Validates if all required fields are present in an object
 * @param {Object} data - The data object to validate
 * @param {Array} requiredFields - Array of required field names
 * @returns {boolean} True if all required fields are present
 */
export const validateRequiredFields = (data, requiredFields) => {
  if (!data || !requiredFields || !requiredFields.length) return false;
  return requiredFields.every(field => data[field] !== undefined && data[field] !== null);
};

/**
 * Creates a deep clone of an object
 * @param {Object} obj - The object to clone
 * @returns {Object} Deep cloned object
 */
export const deepClone = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Debounces a function call
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Creates a throttled version of a function
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Safely parses JSON with fallback
 * @param {string} jsonString - JSON string to parse
 * @param {*} fallback - Fallback value if parsing fails
 * @returns {*} Parsed JSON or fallback
 */
export const safeJsonParse = (jsonString, fallback = {}) => {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('Failed to parse JSON:', error);
    return fallback;
  }
};

/**
 * Generates a unique ID
 * @returns {string} Unique identifier
 */
export const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};